import { useState } from 'react';
import styled from '@/styles';
import Popover from '@/components/Popover';
import { DependabilityV2 } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { categoryColor } from './tierColors';
import PopoverSurface from './PopoverSurface';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FlagSolidIcon,
} from './icons';

const Row = styled('div', {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'center',
});
const BadgeChip = styled('span', {
  // UA-button resets FIRST (the `font` shorthand would otherwise clobber the
  // fontSize/fontWeight below): the pools chip renders `as="button"` so it is
  // reachable by keyboard; these are visual no-ops for the span usages.
  background: 'none',
  font: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '8px',
  fontSize: '$sm',
  fontWeight: 700,
  border: '1px solid transparent',
  variants: {
    kind: {
      positive: {
        color: dependabilityV2Colors.depPositive,
        borderColor: dependabilityV2Colors.depFeedPositive,
      },
      danger: {
        color: dependabilityV2Colors.depTierMonitored,
        backgroundColor: dependabilityV2Colors.depDangerBg,
      },
      // Location flag at N >= 1. Same red fill as `danger` but WITH the stroke the Figma
      // draws on this badge only, so it reads as the most urgent chip in the row.
      flagged: {
        color: dependabilityV2Colors.depTierMonitored,
        backgroundColor: dependabilityV2Colors.depDangerBg,
        borderColor: dependabilityV2Colors.depDangerBorder,
      },
      warning: {
        color: dependabilityV2Colors.depWarnText,
        backgroundColor: dependabilityV2Colors.depWarnBg,
      },
      // "0 preferred pools" is neither good nor bad — an explicit variant so
      // the empty state is a deliberate grey chip rather than whatever the
      // unstyled base happens to inherit.
      neutral: {
        color: dependabilityV2Colors.depInk,
        borderColor: dependabilityV2Colors.depBorder,
      },
    },
  },
});
const PoolList = styled(PopoverSurface, { padding: '12px', minWidth: '200px' });

const StatusIcon = ({ positive }: { positive: boolean }) =>
  positive ? <CheckCircleIcon /> : <ExclamationTriangleIcon />;

interface Props {
  badges: DependabilityV2['badges'];
  /**
   * Opens the Location Flag modal (GRAVY-10365). A MODAL, deliberately not a popover: the
   * AC calls out that the tier badge's info-icon popover must not be reused here.
   * Omitted in isolated component tests, which renders the chip inert.
   */
  onLocationFlagsClick?: () => void;
  /**
   * Outstanding location flags (GRAVY-10365). A PROP rather than a field on
   * `badges`: the overview payload no longer carries this count — it lives on
   * the location-flags summary endpoint, which is the same source the modal
   * header reads, so the badge and the modal cannot disagree.
   *
   * Defaults to 0 so the chip renders its clean state while the summary is
   * still loading, rather than flashing "Location flag (undefined)".
   */
  locationFlagCount?: number;
}

const StatusBadgeRow = ({
  badges,
  onLocationFlagsClick,
  locationFlagCount = 0,
}: Props) => {
  const [poolsOpen, setPoolsOpen] = useState(false);
  const noShowPositive = badges.no_shows_count === 0;
  const latePositive = badges.late_count === 0;
  // GRAVY-10402: all-time late-drop (late cancellation) count. Defaulted so a
  // payload from a backend that predates the field renders "0 late cancels"
  // rather than "undefined late cancels".
  const lateCancelsCount = badges.late_cancels_count ?? 0;
  const lateCancelsPositive = lateCancelsCount === 0;
  const locationFlagsClean = locationFlagCount === 0;
  // preferred_pools can list the same customer twice (favorited AND LTA — the
  // backend has no unique index across kinds), which inflates the "N preferred
  // pools" count and collides React keys. Collapse to one entry per customer_id.
  const uniquePools = Array.from(
    new Map(
      badges.preferred_pools.map((pool) => [pool.customer_id, pool]),
    ).values(),
  );

  return (
    <Row>
      <BadgeChip kind={noShowPositive ? 'positive' : 'danger'}>
        <StatusIcon positive={noShowPositive} />
        {/* GRAVY-10402: ALL-TIME no-show count (not a 30-day window), so it
            reconciles with the lifetime "No Shows" count shown in Worker Details
            on the same screen. "No no-shows" therefore reads as the true total,
            not a rolling window that could hide an older no-show. */}
        {noShowPositive
          ? 'No no-shows'
          : `${badges.no_shows_count} no-show${
              badges.no_shows_count === 1 ? '' : 's'
            }`}
      </BadgeChip>
      <span style={{ position: 'relative' }}>
        {/* A real <button> (fix round 3): the pool list was unreachable by
            keyboard as a styled span with onClick. */}
        {/* Zero pools is an ABSENCE, not an achievement: styled positive with a
            green check it read as a good signal, sitting beside two badges
            where green genuinely means "clean". Neutral when there are none. */}
        <BadgeChip
          as="button"
          type="button"
          kind={uniquePools.length > 0 ? 'positive' : 'neutral'}
          css={{ cursor: 'pointer' }}
          // GRAVY-10233: the SAME clickout race that hid the tier popover (see
          // TierPill). Popover arms a window-level `useClickout` the instant it
          // opens; without stopPropagation the very click that opens this popover
          // keeps bubbling to window, the clickout listener fires, and the
          // popover closes ~1ms after it opened — so a real user click flashed it
          // open then shut ("clicking does nothing"; reproduced on live QA, where
          // a real .click() failed to open it while dispatchEvent did). Toggling
          // here preserves click-again-to-close (previously provided by the same
          // window clickout that now can't see this click).
          onClick={(e) => {
            e.stopPropagation();
            setPoolsOpen((open) => !open);
          }}
          aria-haspopup="dialog"
          aria-expanded={poolsOpen}
          aria-controls="preferred-pools-popover"
          data-testid="preferred-pools-badge"
        >
          {uniquePools.length > 0 && <StatusIcon positive />}
          {`${uniquePools.length} preferred pool${
            uniquePools.length === 1 ? '' : 's'
          }`}
        </BadgeChip>
        <Popover
          id="preferred-pools-popover"
          isOpen={poolsOpen}
          onRequestClose={() => setPoolsOpen(false)}
        >
          <PoolList>
            {uniquePools.length === 0 && <div>No active pool memberships</div>}
            {uniquePools.map((pool) => (
              <div
                key={pool.customer_id}
                style={{ padding: '4px 0', color: categoryColor('neutral') }}
              >
                {pool.customer_name || `Client #${pool.customer_id}`}
              </div>
            ))}
          </PoolList>
        </Popover>
      </span>
      {/* GRAVY-10402: renamed from "late" — this badge counts late ARRIVALS
          (check-ins past the grace window), so "late check-ins" says exactly
          what it measures and no longer collides with late cancellations. */}
      <BadgeChip kind={latePositive ? 'positive' : 'warning'}>
        <StatusIcon positive={latePositive} />
        {`${badges.late_count} late check-in${
          badges.late_count === 1 ? '' : 's'
        }`}
      </BadgeChip>
      {/* GRAVY-10402: NEW — all-time late cancellations (late_drop violations),
          the drop counterpart to the late-check-in badge. Warning-styled at 1+,
          affirmative at 0, like the other all-time badges. */}
      <BadgeChip kind={lateCancelsPositive ? 'positive' : 'warning'}>
        <StatusIcon positive={lateCancelsPositive} />
        {`${lateCancelsCount} late cancel${lateCancelsCount === 1 ? '' : 's'}`}
      </BadgeChip>
      {/* Position 4, per the AC's fixed order: No Show, Preferred, Late, Location flag,
          then the tier badge (which the CARD renders, not this row).

          A real <button> so it is keyboard-reachable, matching the pools chip. Unlike that
          one it opens a modal, so `aria-haspopup="dialog"` without `aria-controls` — the
          dialog does not exist in the DOM until it opens.

          NO WINDOW SUFFIX, unlike its neighbours: this follows the Figma badge design.
          Do NOT re-justify this from the ticket AC — Product confirmed on 2026-08-21 that
          the AC is incorrect here and the Figma is the authority (GRAVY-10365 D3).

          The row is therefore deliberately mixed: the three older badges keep their
          shipped wording ("N late (30D)"), this one uses the Figma's. That was decided,
          not overlooked — leave the other three alone. */}
      <BadgeChip
        as="button"
        type="button"
        kind={locationFlagsClean ? 'positive' : 'flagged'}
        css={{ cursor: onLocationFlagsClick ? 'pointer' : 'default' }}
        onClick={onLocationFlagsClick}
        disabled={!onLocationFlagsClick}
        aria-haspopup="dialog"
        data-testid="location-flags-badge"
      >
        {locationFlagsClean ? <StatusIcon positive /> : <FlagSolidIcon />}
        {`Location flag (${locationFlagCount})`}
      </BadgeChip>
    </Row>
  );
};

export default StatusBadgeRow;
